import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Upload, Camera, Loader2, Eye, EyeOff, Heart, Trash2, X, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { Toaster, toast } from "sonner";
import { EVENTS, EVENT_COLORS, UPLOAD_TEXTS, getDefaultEvent, formatTimestamp } from "./config";
import RegistrationScreen from "./RegistrationScreen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Must match backend's sanitize_folder_name() exactly
const sanitizeName = (name) =>
  name.replace(/[^a-zA-Z0-9\s]/g, "").replace(/\s+/g, "_").toLowerCase();

// ── Theme particle colours ─────────────────────────────────────────────────
const HALDI_COLORS   = ["#FF9800","#FFD700","#FF6B35","#FFC107","#F06292","#FF7043","#FFEB3B","#E91E63","#F48FB1","#FF80AB","#FFD740","#FF6090"];
const VARMALA_COLORS = ["#FF4444","#FF8C00","#FFD700","#FF69B4","#FFFFFF","#FF6B35","#FFC0CB","#E53935","#FFA000"];
const PARTICLE_COUNTS = { mehendi: 20, haldi: 35, sangeet: 38, wedding: 36, reception: 30 };

function ThemeOverlay({ event, paused }) {
  const ev = event?.toLowerCase();

  const particles = useMemo(() => {
    const n = PARTICLE_COUNTS[ev] ?? 0;
    const isHaldi   = ev === "haldi";
    const isWedding = ev === "wedding";
    return Array.from({ length: n }, (_, i) => {
      const varmalaPetal = isWedding && i < Math.floor(n * 0.65);
      return {
        id: i,
        cls:      varmalaPetal ? "varmala-petal" : "theme-particle",
        left:     Math.random() * 100,
        top:      (isHaldi || varmalaPetal) ? -(5 + Math.random() * 30) : Math.random() * 95,
        delay:    Math.random() * 8,
        duration: (isHaldi || varmalaPetal) ? 7 + Math.random() * 6 : 2.5 + Math.random() * 5,
        size:     isHaldi ? 10 + Math.random() * 14 : varmalaPetal ? 8 + Math.random() * 12 : 3 + Math.random() * 9,
        color:    isHaldi   ? HALDI_COLORS[i % HALDI_COLORS.length]
                : varmalaPetal ? VARMALA_COLORS[i % VARMALA_COLORS.length]
                : undefined,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ev]);

  if (!ev || particles.length === 0) return null;

  return (
    <div
      className={`theme-overlay theme-overlay-${ev}${paused ? " paused" : ""}`}
      aria-hidden="true"
    >
      {particles.map((p) => (
        <span
          key={p.id}
          className={p.cls || "theme-particle"}
          style={{
            left:            `${p.left}%`,
            top:             `${p.top}%`,
            animationDelay:  `${p.delay}s`,
            animationDuration:`${p.duration}s`,
            width:           `${p.size}px`,
            height:          `${p.size}px`,
            ...(p.color ? { background: p.color } : {}),
          }}
        />
      ))}
      {ev === "sangeet" && (
        <>
          <div className="disco-beam disco-beam-1" />
          <div className="disco-beam disco-beam-2" />
          <div className="disco-beam disco-beam-3" />
          <div className="disco-beam disco-beam-4" />
          <div className="disco-beam disco-beam-5" />
          <div className="sangeet-spot sangeet-spot-1" />
          <div className="sangeet-spot sangeet-spot-2" />
          <div className="sangeet-spot sangeet-spot-3" />
        </>
      )}
      {ev === "mehendi" && (
        <>
          <div className="mehendi-ring mehendi-ring-1" />
          <div className="mehendi-ring mehendi-ring-2" />
          <div className="mehendi-ring mehendi-ring-3" />
        </>
      )}
      {ev === "haldi" && (
        <>
          <div className="haldi-blob haldi-blob-1" />
          <div className="haldi-blob haldi-blob-2" />
        </>
      )}
      {ev === "wedding" && (
        <>
          {/* Toran — decorative garland across the top */}
          <div className="toran">
            {Array.from({ length: 13 }, (_, i) => (
              <div
                key={i}
                className={`toran-unit${i === 6 ? " toran-center" : ""}`}
                style={{ left: `${(i / 12) * 100}%`, animationDelay: `${(i % 4) * 0.4}s` }}
              >
                <div className="toran-leaf toran-leaf-left" />
                <div className="toran-leaf toran-leaf-mid" />
                <div className="toran-leaf toran-leaf-right" />
                <div className="toran-flower" />
              </div>
            ))}
          </div>
          {/* Sindoor — soft red haze clouds */}
          <div className="sindoor-cloud sindoor-1" />
          <div className="sindoor-cloud sindoor-2" />
          {/* Mangalsutra pendant */}
          <div className="mangalsutra-pendant" />
          {/* Chudha bangle stacks on both sides */}
          <div className="chudha-stack chudha-left" />
          <div className="chudha-stack chudha-right" />
        </>
      )}
    </div>
  );
}

function App() {
  const [photos, setPhotos] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("");
  const [showNameInput, setShowNameInput] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(getDefaultEvent);
  const [eventFilter, setEventFilter] = useState(null);
  const [photosOfMeActive, setPhotosOfMeActive] = useState(false);
  const [photosOfMeSort, setPhotosOfMeSort] = useState("date-desc");
  const [isRegistered, setIsRegistered] = useState(
    () => !!localStorage.getItem("weddingVerified")
  );
  const [selfieUrl, setSelfieUrl] = useState(
    () => localStorage.getItem("weddingSelfieUrl") || null
  );
  const [encodedFaceKeys, setEncodedFaceKeys] = useState([]);

  // Ref to track current photos for slideshow / keyboard nav
  const photosRef = useRef(photos);
  useEffect(() => { photosRef.current = photos; }, [photos]);

  // Sentinel div ref for infinite scroll
  const loadMoreRef = useRef(null);

  const myFaceKey = userName ? sanitizeName(userName) : null;
  const hasFaceEncoding = myFaceKey ? encodedFaceKeys.includes(myFaceKey) : false;

  // Core paginated fetch — reset=true clears photos and starts from page 1
  const fetchPhotosPage = useCallback(async ({ reset = false, cursorOverride = null } = {}) => {
    if (!reset && !hasMore) return;
    if (isLoadingMore && !reset) return;

    const savedName = localStorage.getItem("weddingUserName") || "";
    setIsLoadingMore(true);
    if (reset) setLoading(true);

    try {
      const params = {
        currentUser: savedName,
        cursor:  reset ? undefined : (cursorOverride ?? nextCursor),
        event:   (!photosOfMeActive && eventFilter) ? eventFilter : undefined,
        person:  photosOfMeActive ? myFaceKey : undefined,
        sort:    photosOfMeSort === "date-asc" ? "asc" : "desc",
      };
      // For "Most Liked" sort we still fetch newest and sort client-side on loaded set
      const { data } = await axios.get(`${API}/photos`, { params });
      const incoming = data.photos || [];

      setPhotos(prev => reset ? incoming : [...prev, ...incoming]);
      setNextCursor(data.nextCursor || null);
      setHasMore(!!data.nextCursor);
      setTotalPhotos(data.total || 0);
    } catch (err) {
      console.error("Failed to fetch photos:", err);
    } finally {
      setIsLoadingMore(false);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFilter, photosOfMeActive, photosOfMeSort, myFaceKey, hasMore, isLoadingMore, nextCursor]);

  // Reset feed whenever filter/tab changes
  useEffect(() => {
    fetchPhotosPage({ reset: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFilter, photosOfMeActive, photosOfMeSort]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !isLoadingMore) fetchPhotosPage(); },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, fetchPhotosPage]);

  useEffect(() => {
    const savedName = localStorage.getItem("weddingUserName");
    if (savedName) setUserName(savedName);
    fetchFaceKeys();
    // initial photo load is handled by the filter-change effect above
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFaceKeys = async () => {
    try {
      const { data } = await axios.get(`${API}/face-keys`);
      setEncodedFaceKeys(data.keys || []);
    } catch { /* non-critical */ }
  };

  const handleRegistrationComplete = ({ userName: name, selfieUrl: url }) => {
    localStorage.setItem("weddingUserName", name);
    localStorage.setItem("weddingVerified", "true");
    localStorage.setItem("weddingSelfieUrl", url);
    setUserName(name);
    setSelfieUrl(url);
    setIsRegistered(true);
    fetchFaceKeys(); // refresh so Photos of You tab appears if face was encoded
  };

  const MAX_PHOTOS_PER_DAY = 100;

  const handleUploadClick = () => {
    if (!userName || userName.trim() === "") { setShowNameInput(true); }
    else { document.getElementById("photo-upload").click(); }
  };

  const handleCameraClick = () => {
    if (!userName || userName.trim() === "") { setShowNameInput(true); }
    else { document.getElementById("photo-camera").click(); }
  };

  const handleNameSubmit = () => {
    if (userName && userName.trim() !== "") {
      localStorage.setItem("weddingUserName", userName.trim());
      setShowNameInput(false);
    } else {
      toast.error("Please enter your name");
    }
  };

  const handleFileSelect = async (e) => {
    let files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > MAX_PHOTOS_PER_DAY) {
      toast.warning(`Uploading first ${MAX_PHOTOS_PER_DAY} files.`);
      files = files.slice(0, MAX_PHOTOS_PER_DAY);
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    const newPhotos = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append("file", files[i]);
        formData.append("userName", userName);
        formData.append("eventTag", selectedEvent);
        const { data } = await axios.post(`${API}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        setUploadProgress({ current: i + 1, total: files.length });
        // Build a gallery-shaped object and show it immediately without waiting for Search API
        if (data?.file) {
          const f = data.file;
          // Build thumbnailUrl from secure_url (which has the version baked in) so we
          // get the correct image. build_url() on the backend omits the version and can
          // return wrong/stale content from CDN for a newly uploaded asset.
          const thumbUrl = f.resourceType === "video"
            ? f.url.replace("/upload/", "/upload/c_fill,h_500,q_auto,w_400,f_jpg/")
            : f.url.replace("/upload/", "/upload/c_fill,h_500,q_auto,w_400,f_auto/");
          newPhotos.push({
            id:           f.id,
            encodedId:    f.id.replace(/\//g, "|"),
            name:         f.id.split("/").pop(),
            url:          f.url,
            thumbnailUrl: thumbUrl,
            resourceType: f.resourceType || "image",
            createdTime:  new Date().toISOString(),
            uploader:     userName,
            isPrivate:    false,
            likeCount:    0,
            likedByMe:    false,
            eventTag:     selectedEvent,
            persons:      [],
          });
        }
      }
      // Prepend new photos so they appear instantly (Search API has indexing delay)
      if (newPhotos.length > 0 && !photosOfMeActive) {
        setPhotos(prev => [...newPhotos, ...prev]);
        setTotalPhotos(t => t + newPhotos.length);
      }
      toast.success(`${files.length} item(s) added to ${selectedEvent}`);
    } catch (error) {
      const msg = error?.response?.data?.detail;
      toast.error(msg || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      e.target.value = "";
    }
  };

  const togglePhotoLike = async (photo) => {
    if (!userName) { toast.error("Enter your name first to like"); setShowNameInput(true); return; }
    try {
      const formData = new FormData();
      formData.append("userName", userName);
      const { data } = await axios.post(`${API}/photos/${photo.encodedId}/like`, formData);
      const { likeCount, likedByMe } = data;
      const upd = (p) => (p.id === photo.id ? { ...p, likeCount, likedByMe } : p);
      setPhotos(prev => prev.map(upd));
      setPreviewPhoto(prev => prev?.id === photo.id ? { ...prev, likeCount, likedByMe } : prev);
    } catch { toast.error("Failed to like"); }
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm(`Delete this photo?`)) return;
    setDeletingPhoto(photo.id);
    try {
      await axios.delete(`${API}/photos/${photo.encodedId}`, { data: { userName } });
      toast.success("Deleted");
      setPhotos(prev => prev.filter(p => p.id !== photo.id));
      setTotalPhotos(t => t - 1);
      if (previewPhoto?.id === photo.id) setPreviewPhoto(null);
    } catch { toast.error("Failed to delete"); }
    finally { setDeletingPhoto(null); }
  };

  const togglePhotoPrivacy = async (photo) => {
    try {
      const formData = new FormData();
      formData.append("userName", userName);
      formData.append("isPrivate", !photo.isPrivate);
      await axios.post(`${API}/photos/${photo.encodedId}/privacy`, formData);
      toast.success(photo.isPrivate ? "Now public" : "Now private");
      setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, isPrivate: !p.isPrivate } : p));
    } catch { toast.error("Failed to update privacy"); }
  };

  const openPreview = (photo) => {
    const idx = photosRef.current.findIndex((p) => p.id === photo.id);
    setPreviewIndex(idx >= 0 ? idx : 0);
    setPreviewPhoto(photo);
  };

  const closePreview = () => { setPreviewPhoto(null); setSlideshowActive(false); };

  const previewPrev = () => {
    const list = photosRef.current;
    const newIdx = (previewIndex - 1 + list.length) % list.length;
    setPreviewIndex(newIdx);
    setPreviewPhoto(list[newIdx]);
  };

  const previewNext = () => {
    const list = photosRef.current;
    const newIdx = (previewIndex + 1) % list.length;
    setPreviewIndex(newIdx);
    setPreviewPhoto(list[newIdx]);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (!previewPhoto) return;
      if (e.key === "Escape") closePreview();
      else if (e.key === "ArrowLeft") previewPrev();
      else if (e.key === "ArrowRight") previewNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewPhoto, previewIndex]);

  useEffect(() => {
    if (!slideshowActive) return;
    const interval = setInterval(() => {
      setPreviewIndex((prev) => {
        const list = photosRef.current;
        const newIdx = (prev + 1) % list.length;
        setPreviewPhoto(list[newIdx]);
        return newIdx;
      });
    }, 3500);
    return () => clearInterval(interval);
  }, [slideshowActive]);

  const startSlideshow = () => {
    if (photos.length === 0) return;
    setSlideshowActive(true);
    setPreviewIndex(0);
    setPreviewPhoto(photosRef.current[0]);
  };

  // Sort the loaded photos for "Photos of You" (client-side on loaded set only)
  const displayPhotos = (photosOfMeActive && photosOfMeSort === "likes")
    ? [...photos].sort((a, b) => b.likeCount - a.likeCount)
    : photos;

  const renderPhotoCard = (photo) => {
    const isOwn = photo.uploader === userName;
    const isDeleting = deletingPhoto === photo.id;
    const color = EVENT_COLORS[photo.eventTag];
    return (
      <div key={photo.id} className={`photo-card${photo.resourceType === "video" ? " is-video" : ""}`} data-testid="photo-card">
        <img
          src={photo.thumbnailUrl || photo.url}
          alt={photo.name}
          loading="lazy"
          onClick={(e) => { e.stopPropagation(); openPreview(photo); }}
          onError={(e) => {
            e.target.src = "https://via.placeholder.com/400x300?text=Image";
          }}
        />
        {photo.resourceType === "video" && (
          <div className="video-play-badge" onClick={(e) => { e.stopPropagation(); openPreview(photo); }}>
            <Play size={22} fill="white" stroke="none" />
          </div>
        )}

        {/* Event badge */}
        {photo.eventTag && color && (
          <div
            className="event-badge"
            style={{ background: color.bg }}
          >
            {photo.eventTag}
          </div>
        )}

        {/* Always-visible like bar + timestamp */}
        <div className="photo-like-bar">
          <button
            className={`like-btn ${photo.likedByMe ? "liked" : ""}`}
            onClick={(e) => { e.stopPropagation(); togglePhotoLike(photo); }}
            title="Like"
          >
            <Heart
              size={15}
              fill={photo.likedByMe ? "#FF4D4D" : "none"}
              stroke={photo.likedByMe ? "#FF4D4D" : "white"}
            />
            {photo.likeCount > 0 && <span className="like-count">{photo.likeCount}</span>}
          </button>
          <span className="photo-timestamp">{formatTimestamp(photo.createdTime)}</span>
        </div>

        {/* Owner hover actions */}
        {isOwn && (
          <div className="photo-actions">
            <button
              className="action-btn privacy"
              onClick={() => togglePhotoPrivacy(photo)}
              data-testid="privacy-btn"
              title={photo.isPrivate ? "Make Public" : "Make Private"}
            >
              {photo.isPrivate ? <Eye size={18} /> : <EyeOff size={18} />}
            </button>
            <button
              className="action-btn delete"
              onClick={() => deletePhoto(photo)}
              disabled={isDeleting}
              data-testid="delete-btn"
              title="Delete"
            >
              {isDeleting ? <Loader2 size={18} className="spinning" /> : <Trash2 size={18} />}
            </button>
          </div>
        )}

        {photo.isPrivate && (
          <div className="private-badge" data-testid="private-badge">
            <EyeOff size={12} />
            Private
          </div>
        )}

      </div>
    );
  };

  if (!isRegistered) {
    return <RegistrationScreen onComplete={handleRegistrationComplete} />;
  }

  return (
    <div className={`app-container theme-${selectedEvent.toLowerCase()}${uploading ? " uploading" : ""}`}>
      <ThemeOverlay event={selectedEvent} paused={uploading} />
      <Toaster position="top-center" richColors />

      {/* Hero Section */}
      <div className="hero-card">

        {/* Sangeet: disco light beams on the card */}
        {selectedEvent === "Sangeet" && (
          <div className="card-disco">
            <div className="card-beam card-beam-1" />
            <div className="card-beam card-beam-2" />
            <div className="card-beam card-beam-3" />
            <div className="card-spot card-spot-1" />
            <div className="card-spot card-spot-2" />
          </div>
        )}

        <div className="logo-container">
          <img
            src="/wedding-logo.png"
            alt="PaRiTale Wedding Logo"
            className="wedding-logo"
          />
        </div>

        <h1 className="couple-names">
          Paritosh <Heart className="heart-icon-red" fill="#DC2626" stroke="none" size={32} /> Ridhima
        </h1>

        <p className="hashtag">#PaRiTaleWedding</p>

        <p className="tagline">
          Scan. Smile. Share the love ✨<br />
          Capture every magical moment with us.
        </p>

        {showNameInput && (
          <div className="name-input-card" data-testid="name-input-card">
            <p className="name-label">What's your name?</p>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your name"
              className="name-input"
              data-testid="name-input"
              onKeyDown={(e) => e.key === "Enter" && handleNameSubmit()}
              autoFocus
            />
            <button
              onClick={handleNameSubmit}
              className="name-submit-btn"
              data-testid="name-submit-btn"
            >
              Continue
            </button>
          </div>
        )}

        {!showNameInput && (
          <>
            {selectedEvent && (
              <p className="event-upload-tagline">{UPLOAD_TEXTS[selectedEvent]}</p>
            )}
            <div className="upload-buttons">
              <button
                onClick={handleUploadClick}
                className="upload-btn"
                disabled={uploading}
                data-testid="upload-photos-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="btn-icon spinning" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="btn-icon" />
                    Upload
                  </>
                )}
              </button>
              <button
                onClick={handleCameraClick}
                className="camera-btn"
                disabled={uploading}
                data-testid="camera-btn"
              >
                <Camera className="btn-icon" />
                Capture
              </button>
            </div>

            {/* Upload progress bar */}
            {uploading && uploadProgress.total > 0 && (
              <div className="upload-progress">
                <p className="upload-progress-label">
                  Uploading {uploadProgress.current} of {uploadProgress.total}…
                </p>
                <div className="upload-progress-track">
                  <div
                    className="upload-progress-fill"
                    style={{ width: `${Math.round((uploadProgress.current / uploadProgress.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Event picker — shown when user name is set */}
            {userName && (
              <div className="event-picker">
                <p className="event-picker-label">
                  {uploading ? "Uploading to:" : "Tag for:"}
                </p>
                <div className="event-picker-pills">
                  {EVENTS.map((ev) => {
                    const color = EVENT_COLORS[ev];
                    const isActive = selectedEvent === ev;
                    return (
                      <button
                        key={ev}
                        className={`event-picker-pill ${isActive ? "active" : ""}`}
                        style={isActive ? { background: color.bg, borderColor: color.bg } : {}}
                        onClick={() => {
                          setSelectedEvent(ev);
                          setEventFilter(ev);
                          localStorage.setItem("weddingEventTag", ev);
                        }}
                      >
                        {ev}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {userName && (
              <div className="user-greeting">
                {selfieUrl && (
                  <img src={selfieUrl} alt={userName} className="greeting-avatar" />
                )}
                <span>Welcome, {userName}! 👋</span>
              </div>
            )}
          </>
        )}

        <input
          id="photo-upload"
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileSelect}
          style={{ display: "none" }}
          disabled={uploading}
          data-testid="photo-upload-input"
        />
        <input
          id="photo-camera"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          style={{ display: "none" }}
          disabled={uploading}
          data-testid="photo-camera-input"
        />
      </div>

      {/* Preview / Lightbox */}
      {previewPhoto && (
        <div className="preview-overlay" onClick={closePreview}>
          <button className="preview-close" onClick={closePreview}>
            <X size={28} />
          </button>
          <button
            className={`preview-slideshow-btn ${slideshowActive ? "active" : ""}`}
            onClick={(e) => { e.stopPropagation(); setSlideshowActive((prev) => !prev); }}
            title={slideshowActive ? "Pause slideshow" : "Play slideshow"}
          >
            {slideshowActive ? <Pause size={18} /> : <Play size={18} />}
          </button>
          {photos.length > 1 && (
            <button
              className="preview-nav prev"
              onClick={(e) => { e.stopPropagation(); previewPrev(); }}
            >
              <ChevronLeft size={36} />
            </button>
          )}
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            {previewPhoto.resourceType === "video" ? (
              <video
                src={previewPhoto.url}
                className="preview-image"
                controls
                autoPlay
                playsInline
                loop
              />
            ) : (
              <img
                src={previewPhoto.url}
                alt={previewPhoto.name}
                className="preview-image"
              />
            )}
            <div className="preview-info">
              <span className="preview-uploader">{previewPhoto.uploader}</span>
              {previewPhoto.eventTag && EVENT_COLORS[previewPhoto.eventTag] && (
                <span
                  className="preview-event-badge"
                  style={{ background: EVENT_COLORS[previewPhoto.eventTag].bg }}
                >
                  {previewPhoto.eventTag}
                </span>
              )}
              {previewPhoto.createdTime && (
                <span className="preview-timestamp">
                  {new Date(previewPhoto.createdTime).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              )}
              <button
                className={`preview-like-btn ${previewPhoto.likedByMe ? "liked" : ""}`}
                onClick={(e) => { e.stopPropagation(); togglePhotoLike(previewPhoto); }}
                title="Like"
              >
                <Heart
                  size={20}
                  fill={previewPhoto.likedByMe ? "#FF4D4D" : "none"}
                  stroke={previewPhoto.likedByMe ? "#FF4D4D" : "rgba(255,255,255,0.8)"}
                />
                {previewPhoto.likeCount > 0 && (
                  <span className="preview-like-count">{previewPhoto.likeCount}</span>
                )}
              </button>
              {photos.length > 1 && (
                <span className="preview-counter">
                  {previewIndex + 1} / {photos.length}
                </span>
              )}
            </div>
          </div>
          {photos.length > 1 && (
            <button
              className="preview-nav next"
              onClick={(e) => { e.stopPropagation(); previewNext(); }}
            >
              <ChevronRight size={36} />
            </button>
          )}
        </div>
      )}

      {/* Gallery Section */}
      <div className="gallery-section">
        <div className="gallery-header">
          <h2 className="gallery-title">Wedding Memories ({totalPhotos})</h2>
          {totalPhotos > 0 && (
            <button className="slideshow-btn" onClick={startSlideshow}>
              <Play size={15} />
              Slideshow
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading-state">
            <Loader2 className="spinning" size={40} />
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {/* Primary tabs row */}
            <div className="event-filter-tabs">
              {/* All Events */}
              <button
                className={`event-filter-tab ${!photosOfMeActive && !eventFilter ? "active" : ""}`}
                onClick={() => { setPhotosOfMeActive(false); setEventFilter(null); setSelectedEvent(getDefaultEvent()); }}
              >
                All Events
              </button>

              {/* Photos of You — shown only when user has a face encoding stored */}
              {hasFaceEncoding && (
                <button
                  className={`event-filter-tab photos-of-me-tab ${photosOfMeActive ? "active" : ""}`}
                  onClick={() => { setPhotosOfMeActive(true); setEventFilter(null); }}
                >
                  {selfieUrl && (
                    <img src={selfieUrl} alt="" className="tab-selfie-avatar" />
                  )}
                  Photos of You
                </button>
              )}

              {/* Event tabs */}
              {EVENTS.map((ev) => {
                const color = EVENT_COLORS[ev];
                const isActive = !photosOfMeActive && eventFilter === ev;
                return (
                  <button
                    key={ev}
                    className={`event-filter-tab ${isActive ? "active" : ""}${photosOfMeActive && eventFilter === ev ? " subfilter-active" : ""}`}
                    style={isActive ? { background: color.bg, borderColor: "transparent", color: "white" } : {}}
                    onClick={() => {
                      if (photosOfMeActive) {
                        setEventFilter(eventFilter === ev ? null : ev);
                      } else {
                        setEventFilter(isActive ? null : ev);
                        if (!isActive) setSelectedEvent(ev); else setSelectedEvent(getDefaultEvent());
                      }
                    }}
                  >
                    <span className="event-tab-dot" style={{ background: color.bg }} />
                    {ev}
                  </button>
                );
              })}
            </div>

            {/* Photos of You sort toolbar */}
            {photosOfMeActive && (
              <div className="photos-of-me-toolbar">
                <span className="photos-of-me-label">
                  {selfieUrl && <img src={selfieUrl} alt="" className="toolbar-selfie-avatar" />}
                  {totalPhotos} photo{totalPhotos !== 1 ? "s" : ""} with you
                </span>
                <div className="sort-pills">
                  {[
                    { key: "date-desc", label: "Newest" },
                    { key: "date-asc",  label: "Oldest" },
                    { key: "likes",     label: "Most Liked" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      className={`sort-pill ${photosOfMeSort === key ? "active" : ""}`}
                      onClick={() => setPhotosOfMeSort(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Unified photo grid */}
            {displayPhotos.length === 0 && !isLoadingMore ? (
              <div className="empty-state">
                <p>
                  {photosOfMeActive
                    ? "No photos of you yet — check back as more guests upload!"
                    : eventFilter
                    ? `Nothing here for ${eventFilter} yet.`
                    : "Nothing shared yet. Be the first! 📸"}
                </p>
              </div>
            ) : (
              <div className="flat-photo-grid">
                {displayPhotos.map(renderPhotoCard)}
              </div>
            )}

            {/* Infinite scroll sentinel */}
            <div ref={loadMoreRef} style={{ height: 1 }} />

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className="loading-more">
                <Loader2 className="spinning" size={24} />
                <span>Loading more…</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default App;
